import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { VersionNavigation } from "@/components/VersionNavigation";
import { toast } from "@/hooks/use-toast";
import { useUser } from "../../context/UserContext";
import { useParams } from "react-router-dom";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";

// Helper function to get CSRF token from meta tags
const getCsrfToken = () => {
    const csrfTokenTag = document.querySelector("meta[name='csrf-token']");
    const csrfParamTag = document.querySelector("meta[name='csrf-param']");

    if (!csrfTokenTag || !csrfParamTag) {
        console.error("CSRF token vagy param meta tag nem található!");
        return { token: null, param: null };
    }

    return {
        token: csrfTokenTag.getAttribute("content"),
        param: csrfParamTag.getAttribute("content"),
    };
};

interface VariablesRow {
    english: string;
    accepted_translation?: {
        id: number;
        content: string;
    };
    pending_translations: Array<{
        id: number;
        content: string;
    }>;
}

const Gomb9Page = () => {
    const { user } = useUser();
    const { gameId, versionId } = useParams();
    const [results, setResults] = useState<VariablesRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [lastBracketType, setLastBracketType] = useState<'[' | '{'>('[');
    const [editingTranslation, setEditingTranslation] = useState<number | null>(null);
    const [editedText, setEditedText] = useState<string>('');

    // Improved: ignore variable marker type (e.g. { vs [) and allow whitespace/format differences
    const isVariablesMatch = (english: string, translation: string) => {
        // Accept both {var} and [{var}] as equivalent for the check
        const extractAllVars = (text: string) => {
            const allVars = [];
            const regexes = [/\{([^}]+)\}/g, /\[([^\]]+)\]/g];
            for (const regex of regexes) {
                let match;
                while ((match = regex.exec(text)) !== null) {
                    allVars.push(match[1].replace(/\s+/g, ' ').trim().toLowerCase());
                }
            }
            return allVars;
        };
        const engVars = extractAllVars(english);
        const transVars = extractAllVars(translation);
        if (engVars.length !== transVars.length) return false;
        const sortedEng = [...engVars].sort();
        const sortedTrans = [...transVars].sort();
        for (let i = 0; i < sortedEng.length; i++) {
            if (sortedEng[i] !== sortedTrans[i]) return false;
        }
        return true;
    };

    const handleFetchVariables = async (bracketType: '[' | '{') => {
        setLastBracketType(bracketType);
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`/api/variables-with-brackets?type=${bracketType}`);
            if (!response.ok) throw new Error("API hiba");
            const data = await response.json();
            setResults(data);
        } catch (e: any) {
            setError(e.message || "Ismeretlen hiba");
        } finally {
            setLoading(false);
        }
    };

    // Mutation-ok definiálása
    const deleteTranslationMutation = useMutation({
        mutationFn: async (translationId: number) => {
            const { token } = getCsrfToken();
            if (!token) throw new Error("CSRF token nem található.");
            const response = await fetch(`/api/extracted_texts/${translationId}`, {
                method: "DELETE",
                headers: {
                    "X-CSRF-Token": token,
                    "Content-Type": "application/json",
                },
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Törlés sikertelen");
            }
            return response.json().catch(() => ({ message: "Sikeres" }));
        },
        onSuccess: () => {
            toast({ title: "Sikeres!", description: "Fordítás törölve." });
            // Frissítés
            handleFetchVariables(lastBracketType);
        },
        onError: (error) => {
            toast({ title: "Hiba", description: error.message, variant: "destructive" });
        }
    });

    const revertAcceptanceMutation = useMutation({
        mutationFn: async (translationId: number) => {
            const { token } = getCsrfToken();
            if (!token) throw new Error("CSRF token nem található.");
            const response = await fetch(`/api/extracted_texts/${translationId}/revert_acceptance`, {
                method: "PATCH",
                headers: {
                    "X-CSRF-Token": token,
                    "Content-Type": "application/json",
                },
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Visszavonás sikertelen");
            }
            return response.json();
        },
        onSuccess: () => {
            toast({ title: "Sikeres!", description: "Elfogadás visszavonva." });
            // Frissítés
            handleFetchVariables(lastBracketType);
        },
        onError: (error) => {
            toast({ title: "Hiba", description: error.message, variant: "destructive" });
        }
    });

    // Elfogadás mutation hozzáadása
    const acceptTranslationMutation = useMutation({
        mutationFn: async (translationId: number) => {
            const { token } = getCsrfToken();
            if (!token) throw new Error("CSRF token nem található.");
            const response = await fetch(`/api/extracted_texts/${translationId}/accept_translation`, {
                method: "PATCH",
                headers: {
                    "X-CSRF-Token": token,
                    "Content-Type": "application/json",
                },
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Elfogadás sikertelen");
            }
            return response.json();
        },
        onSuccess: () => {
            toast({ title: "Sikeres!", description: "Fordítás elfogadva." });
            // Frissítés
            handleFetchVariables(lastBracketType);
        },
        onError: (error) => {
            toast({ title: "Hiba", description: error.message, variant: "destructive" });
        }
    });

    // Frissítés mutation hozzáadása
    const updateTranslationMutation = useMutation({
        mutationFn: async ({ translationId, content }: { translationId: number; content: string }) => {
            const { token } = getCsrfToken();
            if (!token) throw new Error("CSRF token nem található.");
            const response = await fetch(`/api/extracted_texts/${translationId}`, {
                method: "PATCH",
                headers: {
                    "X-CSRF-Token": token,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ content }),
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Frissítés sikertelen");
            }
            return response.json();
        },
        onSuccess: () => {
            toast({ title: "Sikeres!", description: "Fordítás frissítve." });
            // Frissítés
            handleFetchVariables(lastBracketType);
        },
        onError: (error) => {
            toast({ title: "Hiba", description: error.message, variant: "destructive" });
        }
    });

    return (
        <div className="p-8">
            <h1 className="text-3xl font-bold mb-6">Változók</h1>

            <VersionNavigation gameId={gameId} versionId={versionId} />

            <div className="flex gap-4 mb-4">
                <Button
                    onClick={() => handleFetchVariables('[')}
                    disabled={loading}
                    variant={lastBracketType === '[' ? "default" : "outline"}
                >
                    [ ] változók lekérése
                </Button>
                <Button
                    onClick={() => handleFetchVariables('{')}
                    disabled={loading}
                    variant={lastBracketType === '{' ? "default" : "outline"}
                >
                    {"{ } változók lekérése"}
                </Button>
            </div>

            {loading && <div className="mt-4 text-muted-foreground">Betöltés...</div>}
            {error && <div className="mt-4 text-red-500">{error}</div>}

            {results.length > 0 && (
                <div className="mt-6">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="min-w-[300px]">Angol szöveg</TableHead>
                                <TableHead className="min-w-[300px]">Fordítás(ok)</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {results.map((row, i) => (
                                <TableRow key={i}>
                                    <TableCell>
                                        <div className="break-words text-sm font-mono">{row.english}</div>
                                    </TableCell>
                                    <TableCell>
                                        <div className="space-y-2">
                                            {/* Elfogadott fordítás */}
                                            {row.accepted_translation && (
                                                editingTranslation === row.accepted_translation.id ? (
                                                    <div key={row.accepted_translation.id} className="bg-blue-50 dark:bg-blue-950 p-2 rounded">
                                                        <Textarea
                                                            value={editedText}
                                                            onChange={(e) => setEditedText(e.target.value)}
                                                            className="w-full min-h-20"
                                                        />
                                                        <div className="flex gap-2 mt-2">
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={async () => {
                                                                    try {
                                                                        await updateTranslationMutation.mutateAsync({ translationId: row.accepted_translation!.id, content: editedText });
                                                                        await acceptTranslationMutation.mutateAsync(row.accepted_translation!.id);
                                                                        setEditingTranslation(null);
                                                                    } catch (error) {
                                                                        // Error handling is done in mutations
                                                                    }
                                                                }}
                                                                disabled={updateTranslationMutation.isPending || acceptTranslationMutation.isPending}
                                                            >
                                                                Mentés és elfogadás
                                                            </Button>
                                                            <Button
                                                                variant="secondary"
                                                                size="sm"
                                                                onClick={() => setEditingTranslation(null)}
                                                            >
                                                                Mégse
                                                            </Button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-start gap-2">
                                                        <div className="flex items-center gap-1 flex-shrink-0 pt-1">
                                                            {!isVariablesMatch(row.english, row.accepted_translation.content) && (
                                                                <span className="text-yellow-500 text-lg" title="A változók nem stimmelnek!">
                                                                    ⚠️
                                                                </span>
                                                            )}
                                                            <span className="font-semibold text-lg text-green-600">✓</span>
                                                        </div>
                                                        <span className={`font-mono break-all text-sm ${isVariablesMatch(row.english, row.accepted_translation.content) ? 'text-green-600' : 'text-red-500'}`}>{row.accepted_translation.content}</span>
                                                        <div className="flex gap-1 ml-auto flex-shrink-0">
                                                            {(user?.admin || user?.translater_admin) && (
                                                                <Button
                                                                    variant="outline"
                                                                    size="sm"
                                                                    className="h-6 px-2"
                                                                    onClick={() => {
                                                                        setEditingTranslation(row.accepted_translation!.id);
                                                                        setEditedText(row.accepted_translation!.content);
                                                                    }}
                                                                    title="Szerkesztés"
                                                                >
                                                                    ✏️
                                                                </Button>
                                                            )}
                                                            <Button
                                                                onClick={() => {
                                                                    if (window.confirm("Biztosan vissza szeretnéd vonni ezt az elfogadást?")) {
                                                                        revertAcceptanceMutation.mutate(row.accepted_translation!.id);
                                                                    }
                                                                }}
                                                                disabled={revertAcceptanceMutation.isPending}
                                                                size="sm"
                                                                variant="secondary"
                                                                className="h-6 px-2 text-orange-500 hover:text-orange-700"
                                                                title="Visszavon"
                                                            >
                                                                ↩️
                                                            </Button>
                                                            <Button
                                                                variant="destructive"
                                                                size="sm"
                                                                className="h-6 px-2"
                                                                onClick={() => {
                                                                    if (window.confirm("Biztosan törölni szeretnéd ezt a fordítást?")) {
                                                                        deleteTranslationMutation.mutate(row.accepted_translation!.id);
                                                                    }
                                                                }}
                                                                disabled={deleteTranslationMutation.isPending}
                                                                title="Törlés"
                                                            >
                                                                🗑️
                                                            </Button>
                                                        </div>
                                                    </div>
                                                )
                                            )}

                                            {/* Pending fordítások */}
                                            {row.pending_translations.map((trans) => (
                                                editingTranslation === trans.id ? (
                                                    <div key={trans.id} className="bg-blue-50 dark:bg-blue-950 p-2 rounded">
                                                        <Textarea
                                                            value={editedText}
                                                            onChange={(e) => setEditedText(e.target.value)}
                                                            className="w-full min-h-20"
                                                        />
                                                        <div className="flex gap-2 mt-2">
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={async () => {
                                                                    try {
                                                                        await updateTranslationMutation.mutateAsync({ translationId: trans.id, content: editedText });
                                                                        await acceptTranslationMutation.mutateAsync(trans.id);
                                                                        setEditingTranslation(null);
                                                                    } catch (error) {
                                                                        // Error handling is done in mutations
                                                                    }
                                                                }}
                                                                disabled={updateTranslationMutation.isPending || acceptTranslationMutation.isPending}
                                                            >
                                                                Mentés és elfogadás
                                                            </Button>
                                                            <Button
                                                                variant="secondary"
                                                                size="sm"
                                                                onClick={() => setEditingTranslation(null)}
                                                            >
                                                                Mégse
                                                            </Button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div key={trans.id} className="flex items-start gap-2">
                                                        <div className="flex items-center gap-1 flex-shrink-0 pt-1">
                                                            {!isVariablesMatch(row.english, trans.content) && (
                                                                <span className="text-yellow-500 text-lg" title="A változók nem stimmelnek!">
                                                                    ⚠️
                                                                </span>
                                                            )}
                                                            <span className="text-lg">⏳</span>
                                                        </div>
                                                        <span className={`font-mono break-all text-sm ${isVariablesMatch(row.english, trans.content) ? '' : 'text-red-500'}`}>{trans.content}</span>
                                                        <div className="flex gap-1 ml-auto flex-shrink-0">
                                                            {user?.admin && (
                                                                <Button
                                                                    variant="outline"
                                                                    size="sm"
                                                                    className="h-6 px-2"
                                                                    onClick={() => {
                                                                        setEditingTranslation(trans.id);
                                                                        setEditedText(trans.content);
                                                                    }}
                                                                    title="Szerkesztés"
                                                                >
                                                                    ✏️
                                                                </Button>
                                                            )}
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                className="h-6 px-2 text-green-500 hover:text-green-700"
                                                                onClick={() => acceptTranslationMutation.mutate(trans.id)}
                                                                disabled={acceptTranslationMutation.isPending}
                                                                title="Elfogad"
                                                            >
                                                                ✓
                                                            </Button>
                                                            <Button
                                                                variant="destructive"
                                                                size="sm"
                                                                className="h-6 px-2"
                                                                onClick={() => {
                                                                    if (window.confirm("Biztosan törölni szeretnéd ezt a fordítást?")) {
                                                                        deleteTranslationMutation.mutate(trans.id);
                                                                    }
                                                                }}
                                                                disabled={deleteTranslationMutation.isPending}
                                                                title="Törlés"
                                                            >
                                                                🗑️
                                                            </Button>
                                                        </div>
                                                    </div>
                                                )
                                            ))}

                                            {!row.accepted_translation && row.pending_translations.length === 0 && (
                                                <span className="text-muted-foreground italic text-sm">Nincs fordítás</span>
                                            )}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}
        </div>
    );
};

export default Gomb9Page;
